import clsx from 'clsx';
import { ReactNode } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import { Stack } from '../Stack';
import * as styles from './PillList.css';
export type PillItemProps = {
  value: string;
  display: ReactNode | ContentId;
  icon?: React.ReactElement;
  disabled?: boolean;
  onPillHover?: (e: React.MouseEvent<HTMLElement>) => void;
  onPillLeave?: (e: React.MouseEvent<HTMLElement>) => void;
  onPillClick?: (id: number, e: React.MouseEvent<HTMLElement>) => void;
  onDelete?: () => void;
  postfixItem?: React.ReactNode;
  customPillContainer?: string;
  useCondensedBorders?: boolean;
  useAnimatedBorder?: boolean;
  error?: boolean;
};

export const PillItem = ({
  value,
  display,
  icon,
  disabled,
  onPillHover,
  onPillLeave,
  onPillClick,
  onDelete,
  postfixItem,
  customPillContainer,
  useCondensedBorders,
  useAnimatedBorder,
  error,
}: PillItemProps) => {
  const { isDarkMode } = useSiteTheme();
  return (
    <div
      className={
        customPillContainer ??
        clsx(styles.pillItemContainer, {
          [styles.condensedBorderRadius]: useCondensedBorders,
          [styles.animatedBorder]: useAnimatedBorder,
          [styles.error]: error,
        })
      }
      key={value}
      title={typeof display === 'string' ? display : undefined}
      onMouseEnter={onPillHover}
      onMouseLeave={onPillLeave}
      onClick={(e) => onPillClick && onPillClick(parseInt(value ?? '0'), e)}
    >
      <Stack
        direction="row"
        gap="m"
        alignItems="center"
        className={clsx(styles.pillItemText, { [styles.errorText]: error })}
      >
        {icon && <Stack alignItems="center">{icon}</Stack>}
        <Content id={typeof display === 'string' ? display : undefined} />
        {postfixItem}
      </Stack>
      {!disabled && (
        <div
          className={clsx(styles.pillDeleteButtonContaienr, {
            [styles.pillDeleteButtonContaienrAnimated]:
              isDarkMode && useAnimatedBorder,
          })}
        >
          <CrossIcon
            withHoverEffect
            onClick={onDelete}
            fill={
              error
                ? vars.color.textError
                : isDarkMode && !useAnimatedBorder
                  ? vars.color.textInvertedV2
                  : undefined
            }
            size={vars.iconSize.s}
          />
        </div>
      )}
      {useAnimatedBorder && (
        <svg xmlns="http://www.w3.org/2000/svg" className={styles.glow}>
          <defs>
            <linearGradient
              id="strokeGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={vars.color.backgroundBrandInverted}>
                <animate
                  attributeName="offset"
                  values="0%;100%;0%"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor={vars.color.backgroundBrand}>
                <animate
                  attributeName="offset"
                  values="100%;200%;100%"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <rect
            className={styles.glowLine}
            stroke={'url(#strokeGradient)'}
            strokeWidth="2px"
            fill="none"
            x="2px"
            y="2px"
            rx="8px"
            pathLength="100"
            strokeLinecap="round"
          ></rect>
        </svg>
      )}
    </div>
  );
};
